import { Link, useNavigate } from "@tanstack/react-router";
import { Check, ChevronDown, Globe, Plus, Settings2 } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useClickOutside } from "@/hooks/use-click-outside";
import { useOrgContext } from "@/hooks/use-org-context";
import {
	type Organization as ApiOrganization,
	useOrganizations,
} from "@/lib/api";
import { organization } from "@/lib/auth";
import { getPublicBootstrapOrganization } from "@/lib/public-bootstrap";
import { signInSearch } from "@/lib/sign-in-redirect";
import { buildOwnerHref } from "@/lib/subdomain";
import { cn } from "@/lib/utils";
import {
	isOrganizationActive,
	resolveOrganizationDisplay,
} from "./organization-dropdown.helpers";

function slugifyOrganizationName(value: string) {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

function WorkspaceRow({
	organization: org,
	isActive,
	onSelect,
	manageable,
	onManageNavigate,
	isPublic,
}: {
	organization: ApiOrganization;
	isActive: boolean;
	onSelect: () => void;
	/** Show the settings cog (only member orgs). */
	manageable?: boolean;
	/** Called before navigation so the dropdown closes. */
	onManageNavigate?: () => void;
	/** Public workspace the caller can browse but is not a member of. */
	isPublic?: boolean;
}) {
	const initial = org.name[0]?.toUpperCase() || "W";

	return (
		<CommandItem
			onSelect={onSelect}
			value={org.name}
			className="group flex w-full cursor-pointer items-center gap-2 px-3 py-2 pr-1 text-sm"
		>
			<div className="h-6 w-6 shrink-0 overflow-hidden rounded bg-muted flex items-center justify-center">
				{org.logo ? (
					<img
						src={org.logo}
						alt=""
						className="h-full w-full object-cover"
						referrerPolicy="no-referrer"
					/>
				) : (
					<span className="text-xs font-medium">{initial}</span>
				)}
			</div>
			<span className="flex-1 truncate text-left">{org.name}</span>
			{isPublic ? (
				<Globe
					className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
					aria-label="Public organization"
				/>
			) : null}
			{manageable ? (
				<Button
					asChild
					variant="ghost"
					size="icon"
					className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
					aria-label={`Settings for ${org.name}`}
				>
					<Link
						to="/$owner/settings/organization"
						params={{ owner: org.slug }}
						onMouseDown={(e) => {
							e.stopPropagation();
						}}
						onClick={(e) => {
							e.stopPropagation();
							onManageNavigate?.();
						}}
					>
						<Settings2 className="h-4 w-4" />
					</Link>
				</Button>
			) : null}
			{isActive && <Check className="h-4 w-4 shrink-0 text-primary" />}
		</CommandItem>
	);
}

export function OrganizationDropdown({
	display,
}: {
	/**
	 * `full` = logo, name and chevron (the panel header); `icon` = logo-only
	 * switcher (narrow top bar, collapsed rail).
	 */
	display: "full" | "icon";
}) {
	const iconOnly = display === "icon";
	const [isOpen, setIsOpen] = useState(false);
	const [showCreateDialog, setShowCreateDialog] = useState(false);
	const [newOrgName, setNewOrgName] = useState("");
	const [newOrgSlug, setNewOrgSlug] = useState("");
	const [slugTouched, setSlugTouched] = useState(false);
	const [createError, setCreateError] = useState<string | null>(null);
	const [isCreating, setIsCreating] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	const { data: allOrgs } = useOrganizations();
	const bootstrapOrg = getPublicBootstrapOrganization();
	const organizations = allOrgs ?? (bootstrapOrg ? [bootstrapOrg] : []);

	const { session, urlOrgSlug, activeOrg } = useOrgContext();
	const isAuthenticated = !!session;

	const sortedOrgs = useMemo(() => {
		const members = organizations.filter((o) => o.is_member);
		const publicOnly = organizations.filter((o) => !o.is_member);
		return [...members, ...publicOnly];
	}, [organizations]);

	const close = useCallback(() => setIsOpen(false), []);
	useClickOutside(dropdownRef, close);

	const goToOwner = (slug: string) => {
		const target = buildOwnerHref(slug);
		if (target.kind === "cross-host") {
			window.location.assign(target.href);
			return;
		}
		navigate({ to: target.to as "/" });
	};

	const handleOrgSwitch = async (
		orgId: string | null,
		slug?: string,
		isMember = true,
	) => {
		if (session) {
			if (isMember && orgId) {
				await organization.setActive({ organizationId: orgId });
				if (slug) {
					goToOwner(slug);
				} else {
					navigate({ to: "/" });
				}
			} else if (slug) {
				goToOwner(slug);
			}
		} else if (slug) {
			goToOwner(slug);
		}
		setIsOpen(false);
	};

	const handleCreateOrg = async () => {
		setCreateError(null);
		if (!newOrgName.trim()) {
			setCreateError("Name is required");
			return;
		}
		setIsCreating(true);
		try {
			const slug = newOrgSlug.trim() || slugifyOrganizationName(newOrgName);
			const res = await organization.create({ name: newOrgName.trim(), slug });
			if (res.error) {
				setCreateError(res.error.message || "Failed to create organization");
			} else {
				setShowCreateDialog(false);
				setNewOrgName("");
				setNewOrgSlug("");
				setSlugTouched(false);
				if (res.data) {
					await organization.setActive({ organizationId: res.data.id });
					goToOwner(res.data.slug);
				}
			}
		} catch (e) {
			setCreateError(e instanceof Error ? e.message : "An error occurred");
		} finally {
			setIsCreating(false);
		}
	};

	const { displayName, displayLogo, displayInitial } =
		resolveOrganizationDisplay({
			isAuthenticated,
			urlOrgSlug,
			activeOrg,
			sessionUser: session?.user,
			organizations,
		});

	const logo = (
		<div className="h-7 w-7 shrink-0 overflow-hidden rounded-lg bg-sidebar-foreground/10 flex items-center justify-center">
			{displayLogo ? (
				<img
					src={displayLogo}
					alt=""
					className="h-full w-full object-cover"
					referrerPolicy="no-referrer"
				/>
			) : (
				<span className="text-sidebar-foreground text-sm font-semibold">
					{displayInitial.toUpperCase()}
				</span>
			)}
		</div>
	);

	return (
		<div
			className={cn("relative min-w-0", iconOnly ? "shrink-0" : "flex-1")}
			ref={dropdownRef}
		>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				aria-label={
					displayName ? `Switch workspace (${displayName})` : "Switch workspace"
				}
				title={iconOnly ? (displayName ?? "Switch workspace") : undefined}
				className={cn(
					"flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 transition-colors hover:bg-sidebar-accent/60",
					iconOnly ? "h-9 w-9 justify-center p-0" : "w-full",
				)}
			>
				{logo}
				{!iconOnly ? (
					<span className="flex-1 truncate text-left font-semibold">
						{displayName}
					</span>
				) : null}
				{!iconOnly ? (
					<ChevronDown
						className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
					/>
				) : null}
			</button>

			{isOpen && (
				<div className="absolute left-0 right-0 top-full z-50 mt-1 min-w-[240px] overflow-hidden rounded-lg border border-border bg-popover ">
					<Command className="bg-transparent">
						<CommandInput
							placeholder="Search organizations..."
							className="h-9"
						/>
						<CommandList className="max-h-[300px]">
							<CommandEmpty>No organizations found.</CommandEmpty>
							{isAuthenticated ? (
								<CommandGroup heading="Organizations">
									{sortedOrgs.map((org) => (
										<WorkspaceRow
											key={org.id}
											organization={org}
											isActive={isOrganizationActive({
												organization: org,
												urlOrgSlug,
												activeOrg,
											})}
											onSelect={() =>
												handleOrgSwitch(org.id, org.slug, org.is_member)
											}
											manageable={org.is_member}
											isPublic={!org.is_member}
											onManageNavigate={() => setIsOpen(false)}
										/>
									))}
								</CommandGroup>
							) : null}
						</CommandList>
						<div className="border-t border-border">
							{isAuthenticated ? (
								<CommandGroup>
									<CommandItem
										onSelect={() => {
											setIsOpen(false);
											setShowCreateDialog(true);
										}}
										className="cursor-pointer text-muted-foreground"
									>
										<Plus className="mr-2 h-4 w-4" />
										<span>Create Organization</span>
									</CommandItem>
								</CommandGroup>
							) : (
								<CommandGroup>
									<CommandItem
										onSelect={() => {
											setIsOpen(false);
											navigate({
												to: "/auth/login",
												search: signInSearch(),
											});
										}}
										className="cursor-pointer text-muted-foreground"
									>
										<Plus className="mr-2 h-4 w-4" />
										<span>Sign in to create organization</span>
									</CommandItem>
								</CommandGroup>
							)}
						</div>
					</Command>
				</div>
			)}

			<Dialog
				open={showCreateDialog}
				onOpenChange={(open) => {
					if (!open) {
						setShowCreateDialog(false);
						setCreateError(null);
					}
				}}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Create Organization</DialogTitle>
						<DialogDescription>
							Create a new organization to collaborate with others.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="space-y-1.5">
							<Label htmlFor="org-name">Name</Label>
							<Input
								id="org-name"
								value={newOrgName}
								onChange={(e) => {
									setNewOrgName(e.target.value);
									if (!slugTouched) {
										setNewOrgSlug(slugifyOrganizationName(e.target.value));
									}
								}}
								placeholder="e.g. My Team"
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="org-slug">Slug</Label>
							<Input
								id="org-slug"
								value={newOrgSlug}
								onChange={(e) => {
									setSlugTouched(true);
									setNewOrgSlug(e.target.value);
								}}
								placeholder="e.g. my-team"
							/>
						</div>
						{createError && (
							<div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
								{createError}
							</div>
						)}
					</div>
					<div className="flex justify-end gap-2">
						<Button
							variant="outline"
							onClick={() => setShowCreateDialog(false)}
							disabled={isCreating}
						>
							Cancel
						</Button>
						<Button onClick={handleCreateOrg} disabled={isCreating}>
							{isCreating ? "Creating..." : "Create"}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
